import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { differenceInDays, format, addDays } from 'date-fns';

import { activityStore } from '../../../../../store/activity.store';
import { ActivityType, Interval } from '../../../../../model/activity.model';
import convertDecimalToHexColor from '../../../../../utils/convertDecimalToHexColor';
import { Alert } from '../../../../ui/alert';
import './BlockyCalendarView.css';

const SLOT_MINUTES = 15;
const SLOTS_PER_HOUR = 4; // 60 / 15
const HOURS_PER_DAY = 24;
const MAX_CALENDAR_DAYS = 7;
const HOVER_DELAY_MS = 400;

// Pre-computed hours array (0-23)
const HOURS = Array.from({ length: HOURS_PER_DAY }, (_, i) => i);

// Helper to format time
const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Helper to format hour label (e.g., "9 AM" or "09:00" depending on locale)
const formatHourLabel = (hour: number) => {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric' });
};

// Format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface HoverInfo {
  x: number;
  y: number;
  activityName: string;
  start: Date;
  end: Date;
  color: string;
  isOccurrence: boolean;
}

const BlockyCalendarView: React.FC = () => {
  const { formatMessage } = useIntl();
  const daysIntervals = activityStore(state => state.daysIntervals || []);
  const activityTypes = activityStore(state => state.activityTypes);
  const intervalsParams = activityStore(state => state.intervalsParams);
  const setActivityToEdit = activityStore(state => state.setActivityToEdit);
  const getActivityDetails = activityStore(state => state.getActivityDetails);

  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate date range
  const { days, exceedsLimit, actualDays, calendarStartDate, calendarEndDate } = useMemo(() => {
    if (!intervalsParams.from || !intervalsParams.to) {
      const today = new Date();
      return {
        days: [today],
        exceedsLimit: false,
        actualDays: 1,
        calendarStartDate: formatLocalDate(today),
        calendarEndDate: formatLocalDate(today)
      };
    }

    const rangeStart = new Date(intervalsParams.from);
    const rangeEnd = new Date(intervalsParams.to);
    const totalDays = differenceInDays(rangeEnd, rangeStart) + 1;
    const exceeds = totalDays > MAX_CALENDAR_DAYS;

    let startDate = rangeStart;
    if (exceeds) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (today >= rangeStart && today <= rangeEnd) {
        const daysFromStart = differenceInDays(today, rangeStart) + 1;
        if (daysFromStart >= MAX_CALENDAR_DAYS) {
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - (MAX_CALENDAR_DAYS - 1));
        }
      } else if (today > rangeEnd) {
        startDate = new Date(rangeEnd);
        startDate.setDate(startDate.getDate() - (MAX_CALENDAR_DAYS - 1));
      }
    }

    const numDays = Math.min(totalDays, MAX_CALENDAR_DAYS);
    const daysArray: Date[] = [];
    for (let i = 0; i < numDays; i++) {
      daysArray.push(addDays(startDate, i));
    }

    const endDate = addDays(startDate, numDays - 1);

    return {
      days: daysArray,
      exceedsLimit: exceeds,
      actualDays: totalDays,
      calendarStartDate: formatLocalDate(startDate),
      calendarEndDate: formatLocalDate(endDate)
    };
  }, [intervalsParams.from, intervalsParams.to]);

  // O(1) lookup map for activity types
  const activityTypesMap = useMemo(() =>
    new Map(activityTypes?.map(t => [t.id, t]) || []),
    [activityTypes]
  );

  // Build interval lookup map: key = "YYYY-MM-DD-hour-slot" -> activity data with fill positions
  const intervalMap = useMemo(() => {
    const map = new Map<string, { interval: Interval; activityType: ActivityType | null; startPercent: number; endPercent: number }>();

    daysIntervals.forEach(dayInterval => {
      dayInterval.intervals.forEach(interval => {
        const activityType = activityTypesMap.get(interval.typeId) || null;
        const start = new Date(interval.start);
        const end = new Date(interval.finish);

        // Calculate which slots this interval covers and by how much
        let slotStart = new Date(start);
        slotStart.setMinutes(Math.floor(slotStart.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES, 0, 0);

        while (slotStart < end) {
          const slotEnd = new Date(slotStart.getTime() + SLOT_MINUTES * 60 * 1000);
          const slotDuration = SLOT_MINUTES * 60 * 1000;

          // Calculate where activity starts and ends within this slot (as percentage)
          const activityStartInSlot = Math.max(0, start.getTime() - slotStart.getTime());
          const activityEndInSlot = Math.min(slotDuration, end.getTime() - slotStart.getTime());

          const startPercent = Math.round((activityStartInSlot / slotDuration) * 100);
          const endPercent = Math.round((activityEndInSlot / slotDuration) * 100);

          if (endPercent > startPercent) {
            const dateKey = formatLocalDate(slotStart);
            const hour = slotStart.getHours();
            const slot = Math.floor(slotStart.getMinutes() / SLOT_MINUTES);
            const key = `${dateKey}-${hour}-${slot}`;

            // If slot already has data, could merge but for now just keep first
            if (!map.has(key)) {
              map.set(key, { interval, activityType, startPercent, endPercent });
            }
          }

          slotStart = slotEnd;
        }
      });
    });

    return map;
  }, [daysIntervals, activityTypesMap]);

  // Get cell data
  const getCellData = useCallback((dayIndex: number, hour: number, slot: number) => {
    const day = days[dayIndex];
    if (!day) return null;
    const key = `${formatLocalDate(day)}-${hour}-${slot}`;
    return intervalMap.get(key) || null;
  }, [days, intervalMap]);

  // Handle cell hover for popover
  const handleCellMouseEnter = useCallback((e: React.MouseEvent, cellData: { interval: Interval; activityType: ActivityType | null; startPercent: number; endPercent: number }) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    const rect = (e.target as HTMLElement).getBoundingClientRect();

    hoverTimeoutRef.current = setTimeout(() => {
      if (cellData.activityType) {
        setHoverInfo({
          x: rect.left + rect.width / 2,
          y: rect.top,
          activityName: cellData.activityType.name,
          start: new Date(cellData.interval.start),
          end: new Date(cellData.interval.finish),
          color: convertDecimalToHexColor(cellData.activityType.color),
          isOccurrence: !!cellData.activityType.occurrence,
        });
      }
    }, HOVER_DELAY_MS);
  }, []);

  const handleCellMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoverInfo(null);
  }, []);

  // Handle click on activity cell to open edit
  const handleCellClick = useCallback((activityId: string) => {
    getActivityDetails(activityId, setActivityToEdit);
  }, [getActivityDetails, setActivityToEdit]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="blocky-calendar-container rounded-lg border border-border/50 bg-card p-4 flex-1 flex flex-col overflow-hidden">
      {exceedsLimit && (
        <Alert
          variant="warning"
          className="mb-2"
          message={formatMessage(
            { id: 'CALENDAR_LIMIT_WARNING' },
            {
              days: MAX_CALENDAR_DAYS,
              selected: actualDays,
              showingFrom: new Date(calendarStartDate).toLocaleDateString(),
              showingTo: new Date(calendarEndDate).toLocaleDateString()
            }
          )}
        />
      )}

      <div className="blocky-wrapper">
        <div className="blocky-grid-container">
          {/* Time labels column */}
          <div className="blocky-time-column">
            <div className="blocky-corner"></div>
            {HOURS.map(hour => (
              <div key={hour} className="blocky-hour-label">
                {formatHourLabel(hour)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, dayIndex) => (
            <div key={dayIndex} className="blocky-day">
              {/* Day header */}
              <div className="blocky-day-header">
                <span className="blocky-day-name">{format(day, 'MMM')}</span>
                <span className="blocky-day-date">{format(day, 'd')}</span>
                <span className="blocky-day-weekday">{format(day, 'EEE')}</span>
              </div>

              {/* Hour rows with 4 slots each */}
              {HOURS.map(hour => (
                <div key={hour} className="blocky-hour-row">
                  {Array.from({ length: SLOTS_PER_HOUR }, (_, slot) => {
                    const cellData = getCellData(dayIndex, hour, slot);
                    const color = cellData?.activityType
                      ? convertDecimalToHexColor(cellData.activityType.color)
                      : undefined;
                    const startPct = cellData?.startPercent ?? 0;
                    const endPct = cellData?.endPercent ?? 0;
                    const isPartial = startPct > 0 || endPct < 100;

                    // Use gradient for partial fills (no border for partial cells)
                    const cellStyle: React.CSSProperties = color
                      ? isPartial
                        ? {
                            background: `linear-gradient(to right, var(--blocky-bg) ${startPct}%, ${color} ${startPct}%, ${color} ${endPct}%, var(--blocky-bg) ${endPct}%)`,
                            border: 'none'
                          }
                        : { backgroundColor: color }
                      : {};

                    return (
                      <div
                        key={slot}
                        className={`blocky-cell ${cellData ? 'has-activity' : ''} ${isPartial && color ? 'partial' : ''}`}
                        style={cellStyle}
                        onClick={cellData ? () => handleCellClick(cellData.interval.activityId) : undefined}
                        onMouseEnter={cellData ? (e) => handleCellMouseEnter(e, cellData) : undefined}
                        onMouseLeave={cellData ? handleCellMouseLeave : undefined}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Activity hover popover */}
        {hoverInfo && (
          <div
            className="blocky-hover-popover"
            style={{
              left: hoverInfo.x,
              top: hoverInfo.y,
            }}
          >
            <div className="blocky-popover-color" style={{ backgroundColor: hoverInfo.color }} />
            <div className="blocky-popover-content">
              <div className="blocky-popover-name">{hoverInfo.activityName}</div>
              <div className="blocky-popover-time">
                {hoverInfo.isOccurrence
                  ? <>{formatTime(hoverInfo.start)} &times;1</>
                  : <>{formatTime(hoverInfo.start)} - {formatTime(hoverInfo.end)}</>
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockyCalendarView;
